/* ──────────────────────────────────────────────────────────────────────────
 *  Clarity adapter — sets custom tags and fires custom events via the
 *  Clarity JS API so they appear in session recordings / dashboards.
 * ────────────────────────────────────────────────────────────────────────── */

import type {
  AnalyticsAdapter,
  AnalyticsEventName,
  AnalyticsEventMap,
} from "../types";

declare global {
  interface Window {
    clarity?: (...args: unknown[]) => void;
  }
}

const CLARITY_ID = process.env.NEXT_PUBLIC_CLARITY_ID ?? "";

function cl(...args: unknown[]) {
  window.clarity?.(...args);
}

const ECOMMERCE_EVENTS = new Set([
  "view_item",
  "add_to_cart",
  "remove_from_cart",
  "view_cart",
  "begin_checkout",
  "purchase",
]);

export const clarityAdapter: AnalyticsAdapter = {
  name: "clarity",

  init() {
    /* Clarity script is loaded separately by the MicrosoftClarity component.
       Nothing extra needed here. */
  },

  track<E extends AnalyticsEventName>(
    event: E,
    properties: AnalyticsEventMap[E],
  ) {
    if (!CLARITY_ID || typeof window === "undefined" || !window.clarity) return;

    cl("event", event);

    if (ECOMMERCE_EVENTS.has(event)) {
      const props = properties as Record<string, unknown>;
      if (props.value !== undefined) {
        cl("set", "order_value", String(props.value));
      }
      if (props.transaction_id) {
        cl("set", "order_id", String(props.transaction_id));
      }
    }

    if (event === "search") {
      const props = properties as { search_term: string };
      cl("set", "search_term", props.search_term);
    }
  },

  identify(userId: string) {
    if (!CLARITY_ID || typeof window === "undefined") return;
    cl("identify", userId);
  },

  reset() {
    /* Clarity does not support identity reset */
  },
};
